import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, RefreshCw, Star } from "lucide-react";
import { apiFetch, getToken } from "@/api";

const fmtINR = (paise) =>
  `₹${((paise || 0) / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const fmtDateTime = (iso) => {
  if (!iso) return "Never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Never";
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const StatCard = ({ label, value, sub, testid }) => (
  <div
    data-testid={testid}
    className="rounded-2xl border border-[#E3ECE6] bg-white p-5"
  >
    <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#647067]">
      {label}
    </p>
    <p className="mt-2 font-display text-3xl font-extrabold text-[#17201A]">
      {value}
    </p>
    {sub && <p className="mt-1 text-xs font-semibold text-[#94A39B]">{sub}</p>}
  </div>
);

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-lg font-extrabold text-[#17201A]">
      {title}
    </h2>
    {children}
  </section>
);

const Breakdown = ({ data, money }) => {
  const entries = Object.entries(data || {});
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div className="mt-4 space-y-3 rounded-2xl border border-[#E3ECE6] bg-white p-5">
      {entries.map(([k, v]) => (
        <div key={k} data-testid={`breakdown-${k.toLowerCase()}`}>
          <div className="flex items-center justify-between text-sm">
            <span className="font-bold text-[#17201A]">{k}</span>
            <span className="font-bold text-[#647067]">
              {money ? fmtINR(v) : v}
            </span>
          </div>
          <div className="mt-1.5 h-2 rounded-full bg-[#F0F4F1]">
            <div
              className="h-2 rounded-full bg-[#16A34A]"
              style={{ width: `${Math.max(3, (v / max) * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [denied, setDenied] = useState(false);
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    plan: "",
    activity: "",
    signup_from: "",
    signup_to: "",
  });

  useEffect(() => {
    if (!getToken()) {
      navigate("/login");
      return;
    }
    apiFetch("/me")
      .then((u) => {
        setMe(u);
        if (!u.isAdmin) setDenied(true);
      })
      .catch(() => navigate("/login"));
  }, [navigate]);

  const loadOverview = useCallback(() => {
    apiFetch("/admin/overview").then(setOverview).catch(() => setDenied(true));
  }, []);

  const loadUsers = useCallback(() => {
    const params = new URLSearchParams({ page: String(page), limit: "10" });
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    apiFetch(`/admin/users?${params}`).then(setUsers).catch(() => {});
  }, [page, filters]);

  useEffect(() => {
    if (me?.isAdmin) {
      loadOverview();
    }
  }, [me, loadOverview]);

  useEffect(() => {
    if (me?.isAdmin) loadUsers();
  }, [me, loadUsers]);

  if (denied) {
    return (
      <div
        data-testid="admin-denied"
        className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#F8FAF7] px-6 text-center"
      >
        <ShieldAlert className="h-10 w-10 text-[#DC2626]" />
        <h1 className="font-display text-2xl font-extrabold text-[#17201A]">
          Admins only
        </h1>
        <p className="max-w-sm text-sm font-semibold text-[#647067]">
          This area is restricted to the Abhyas owner. Your account does not
          have admin access.
        </p>
        <button
          data-testid="admin-denied-home"
          onClick={() => navigate("/")}
          className="rounded-full bg-[#16A34A] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#15803D]"
        >
          Back to home
        </button>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F8FAF7]">
        <p className="text-sm font-bold text-[#647067]">Loading admin data…</p>
      </div>
    );
  }

  const { users: u, subscriptions: s, revenue: r, activity: a, reviews: rv, promptLearning: pl = {} } = overview;

  return (
    <div className="min-h-screen bg-[#F8FAF7] pb-16">
      <header className="border-b border-[#E3ECE6] bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <div>
            <h1
              data-testid="admin-title"
              className="font-display text-2xl font-extrabold text-[#17201A]"
            >
              Admin Dashboard
            </h1>
            <p className="text-xs font-semibold text-[#647067]">
              Abhyas owner console — live data from the database
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-pyq-link"
              onClick={() => navigate("/admin/pyq")}
              className="inline-flex items-center gap-2 rounded-full border border-[#E3ECE6] bg-white px-4 py-2 text-xs font-bold text-[#17201A] hover:border-[#16A34A]"
            >
              PYQ Papers
            </button>
            <button
              data-testid="admin-refresh"
              onClick={() => {
                loadOverview();
                loadUsers();
              }}
              className="inline-flex items-center gap-2 rounded-full border border-[#E3ECE6] bg-white px-4 py-2 text-xs font-bold text-[#17201A] hover:border-[#16A34A]"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6">
        <Section title="Users">
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            <StatCard testid="stat-total-users" label="Total Users" value={u.total} />
            <StatCard testid="stat-signups-today" label="Signups Today" value={u.signupsToday} />
            <StatCard testid="stat-signups-week" label="Signups This Week" value={u.signupsWeek} sub="last 7 days" />
            <StatCard testid="stat-signups-month" label="Signups This Month" value={u.signupsMonth} sub="calendar month" />
            <StatCard testid="stat-active-users" label="Active Users" value={u.activeUsers} sub="active in 30 days" />
            <StatCard testid="stat-dau" label="DAU" value={u.dau} sub="active today" />
            <StatCard testid="stat-wau" label="WAU" value={u.wau} sub="active in 7 days" />
            <StatCard testid="stat-mau" label="MAU" value={u.mau} sub="active in 30 days" />
            <StatCard testid="stat-inactive" label="Inactive Users" value={u.inactiveUsers} sub="no activity in 30+ days" />
          </div>
        </Section>

        <Section title="Subscriptions">
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            <StatCard testid="stat-free-users" label="Free Users" value={s.freeUsers} />
            <StatCard testid="stat-paid-users" label="Paid Users" value={s.paidUsers} />
            <StatCard testid="stat-paid-pct" label="Paid Percentage" value={`${s.paidPercentage}%`} />
            <StatCard testid="stat-active-subs" label="Active Subscriptions" value={s.activeSubscriptions} />
            <StatCard testid="stat-expiring" label="Expiring Soon" value={s.expiringSoon} sub="within 7 days" />
          </div>
          <h3 className="mt-6 text-sm font-extrabold text-[#17201A]">Users by Plan</h3>
          <Breakdown data={s.planBreakdown} />
        </Section>

        <Section title="Revenue">
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            <StatCard testid="stat-rev-total" label="Total Revenue" value={fmtINR(r.total)} />
            <StatCard testid="stat-rev-today" label="Revenue Today" value={fmtINR(r.today)} />
            <StatCard testid="stat-rev-week" label="Revenue This Week" value={fmtINR(r.week)} sub="last 7 days" />
            <StatCard testid="stat-rev-month" label="Revenue This Month" value={fmtINR(r.month)} />
            <StatCard testid="stat-rev-year" label="Revenue This Year" value={fmtINR(r.year)} />
            <StatCard testid="stat-payments" label="Successful Payments" value={r.successfulPayments} sub="failed/cancelled excluded" />
            <StatCard testid="stat-arppu" label="Avg Revenue / Paid User" value={fmtINR(r.averagePerPaidUser)} />
          </div>
          <h3 className="mt-6 text-sm font-extrabold text-[#17201A]">Revenue by Plan</h3>
          <Breakdown data={r.byPlan} money />
        </Section>

        <Section title="User Activity">
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            <StatCard testid="stat-sessions" label="Practice Sessions" value={a.practiceSessions} />
            <StatCard testid="stat-questions" label="Questions Generated" value={a.questionsGenerated} />
            <StatCard testid="stat-evals" label="AI Evaluations" value={a.aiEvaluations} />
            <StatCard testid="stat-videos" label="Video Usage" value={a.videoUsage} sub="Watch & Learn fetches" />
            <StatCard testid="stat-streaks" label="Streak Activity" value={a.streakUsers} sub="users on a streak" />
          </div>
        </Section>

        <Section title="Prompt Learning (Ask Abhyas)">
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            <StatCard testid="stat-ai-requests-today" label="AI Requests Today" value={pl.requestsToday ?? 0} />
            <StatCard testid="stat-ai-requests-month" label="AI Requests This Month" value={pl.requestsMonth ?? 0} />
            <StatCard testid="stat-ai-active-users" label="Active AI Users" value={pl.activeUsers ?? 0} sub="this month" />
            <StatCard testid="stat-ai-credits-consumed" label="Credits Consumed" value={pl.creditsConsumed ?? 0} sub="all time" />
            <StatCard testid="stat-ai-credits-outstanding" label="Credits Outstanding" value={pl.creditsOutstanding ?? 0} sub="unused, in wallets" />
            <StatCard testid="stat-ai-conversations" label="Conversations" value={pl.conversations ?? 0} sub="in chat history" />
            <StatCard testid="stat-ai-conversations-month" label="New Chats This Month" value={pl.conversationsMonth ?? 0} />
            <StatCard testid="stat-ai-retained" label="Retained Chats" value={pl.conversationsWithExpiry ?? 0} sub="paid plans · 30-day retention" />
            <StatCard testid="stat-ai-split" label="Normal / Heavy" value={`${pl.normalRequests ?? 0} / ${pl.heavyRequests ?? 0}`} sub="1-credit / 2-credit requests" />
            <StatCard testid="stat-ai-error-rate" label="Error Rate" value={`${pl.errorRate ?? 0}%`} sub="failed requests cost 0 credits" />
          </div>
          <p className="mt-3 text-xs font-semibold text-[#94A39B]">
            Model: {pl.model || "gemini"} · Tokens: {(pl.totalTokens ?? 0).toLocaleString("en-IN")} total ({(pl.inputTokens ?? 0).toLocaleString("en-IN")} in / {(pl.outputTokens ?? 0).toLocaleString("en-IN")} out)
          </p>
        </Section>

        <Section title="Student Reviews">
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3">
            <StatCard
              testid="stat-avg-rating"
              label="Average Rating"
              value={rv.total ? `${rv.average} / 5` : "—"}
              sub={rv.total ? "across all reviews" : "no reviews yet"}
            />
            <StatCard testid="stat-total-reviews" label="Total Reviews" value={rv.total} />
          </div>
          <div className="mt-4 space-y-3">
            {(rv.latest || []).map((rev, i) => (
              <div
                key={i}
                data-testid={`review-item-${i}`}
                className="rounded-2xl border border-[#E3ECE6] bg-white p-5"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-4 w-4 ${
                          star <= rev.rating
                            ? "fill-[#FACC15] text-[#FACC15]"
                            : "fill-[#E5EFE5] text-[#E5EFE5]"
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-xs font-semibold text-[#94A39B]">
                    {fmtDateTime(rev.createdAt)}
                  </span>
                </div>
                {rev.reviewText && (
                  <p className="mt-2 text-sm font-semibold text-[#17201A]">
                    "{rev.reviewText}"
                  </p>
                )}
                <p className="mt-1.5 text-xs font-bold text-[#647067]">{rev.user}</p>
              </div>
            ))}
            {rv.total === 0 && (
              <p
                data-testid="reviews-empty"
                className="rounded-2xl border border-[#E3ECE6] bg-white p-6 text-center text-sm font-semibold text-[#94A39B]"
              >
                No reviews yet — students are asked to rate Abhyas after every 3rd
                practice session.
              </p>
            )}
          </div>
        </Section>

        <Section title="User List">
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <input
              data-testid="filter-search"
              value={filters.search}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, search: e.target.value });
              }}
              placeholder="Search name or email"
              className="w-56 rounded-full border border-[#E3ECE6] bg-white px-4 py-2 text-sm font-semibold text-[#17201A] outline-none focus:border-[#16A34A]"
            />
            <select
              data-testid="filter-status"
              value={filters.status}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, status: e.target.value });
              }}
              className="rounded-full border border-[#E3ECE6] bg-white px-4 py-2 text-sm font-semibold text-[#17201A]"
            >
              <option value="">All users</option>
              <option value="paid">Paid</option>
              <option value="free">Free</option>
            </select>
            <select
              data-testid="filter-plan"
              value={filters.plan}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, plan: e.target.value });
              }}
              className="rounded-full border border-[#E3ECE6] bg-white px-4 py-2 text-sm font-semibold text-[#17201A]"
            >
              <option value="">All plans</option>
              <option value="Free">Free</option>
              <option value="Starter">Starter</option>
              <option value="Pro">Pro</option>
              <option value="Elite">Elite</option>
            </select>
            <select
              data-testid="filter-activity"
              value={filters.activity}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, activity: e.target.value });
              }}
              className="rounded-full border border-[#E3ECE6] bg-white px-4 py-2 text-sm font-semibold text-[#17201A]"
            >
              <option value="">Any activity</option>
              <option value="active">Active (30d)</option>
              <option value="inactive">Inactive</option>
            </select>
            <input
              data-testid="filter-from"
              type="date"
              value={filters.signup_from}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, signup_from: e.target.value });
              }}
              className="rounded-full border border-[#E3ECE6] bg-white px-4 py-2 text-sm font-semibold text-[#17201A]"
            />
            <input
              data-testid="filter-to"
              type="date"
              value={filters.signup_to}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, signup_to: e.target.value });
              }}
              className="rounded-full border border-[#E3ECE6] bg-white px-4 py-2 text-sm font-semibold text-[#17201A]"
            />
          </div>

          <div className="mt-4 overflow-x-auto rounded-2xl border border-[#E3ECE6] bg-white">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead>
                <tr className="border-b border-[#E3ECE6] text-[11px] font-bold uppercase tracking-[0.12em] text-[#647067]">
                  <th className="px-5 py-3">User</th>
                  <th className="px-5 py-3">Signup</th>
                  <th className="px-5 py-3">Plan</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Last Active</th>
                  <th className="px-5 py-3">Usage</th>
                </tr>
              </thead>
              <tbody>
                {(users?.users || []).map((row) => (
                  <tr
                    key={row.id}
                    data-testid={`user-row-${row.id}`}
                    className="border-b border-[#F0F4F1] last:border-0"
                  >
                    <td className="px-5 py-3">
                      <p className="font-bold text-[#17201A]">{row.name}</p>
                      <p className="text-xs font-semibold text-[#94A39B]">{row.email}</p>
                    </td>
                    <td className="px-5 py-3 font-semibold text-[#647067]">
                      {fmtDate(row.signupDate)}
                    </td>
                    <td className="px-5 py-3 font-bold text-[#17201A]">{row.plan}</td>
                    <td className="px-5 py-3">
                      <span
                        data-testid={`user-status-${row.id}`}
                        className={`inline-block rounded-full px-2.5 py-1 text-[11px] font-extrabold ${
                          row.isPaid
                            ? "bg-[#16A34A]/10 text-[#16A34A]"
                            : "bg-[#F0F4F1] text-[#647067]"
                        }`}
                      >
                        {row.isPaid ? "Paid" : "Free"}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-semibold text-[#647067]">
                      {fmtDateTime(row.lastActiveAt)}
                    </td>
                    <td className="px-5 py-3 font-semibold text-[#647067]">
                      {row.sessions} sessions · {row.questions} Qs
                    </td>
                  </tr>
                ))}
                {users && users.users.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      data-testid="users-empty"
                      className="px-5 py-8 text-center font-semibold text-[#94A39B]"
                    >
                      No users match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <p data-testid="users-total" className="text-xs font-bold text-[#647067]">
              {users?.total ?? 0} users · page {users?.page ?? 1} of {users?.pages ?? 1}
            </p>
            <div className="flex gap-2">
              <button
                data-testid="users-prev"
                disabled={!users || page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="rounded-full border border-[#E3ECE6] bg-white px-4 py-2 text-xs font-bold text-[#17201A] disabled:opacity-40"
              >
                Prev
              </button>
              <button
                data-testid="users-next"
                disabled={!users || page >= users.pages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-full border border-[#E3ECE6] bg-white px-4 py-2 text-xs font-bold text-[#17201A] disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </Section>
      </main>
    </div>
  );
};

export default AdminDashboard;
